/**
 * Turns a mic-test specialty transcript into a two-way clinician ↔ patient
 * discussion for the live-transcription treatment (option-6).
 *
 * Preferred input is a **speaker-tagged** transcript — lines prefixed with
 * `Clinician:` / `Patient:` (authored per specialty in ml-scribe). When the
 * transcript has no speaker tags (specialties not yet converted, or the shared
 * `default`), it falls back to splitting the single narration into sentences and
 * alternating speakers so something reasonable still renders.
 *
 * Each turn keeps the `startIndex` of its first word within the flat word list
 * (speaker labels excluded), so the preview's word-by-word reveal — driven by
 * `revealedWordCount` over that same flat list — stays aligned across turns.
 *
 * Presentation only: the note in the next step comes from a separate `note`
 * asset and is unaffected by how the transcript is displayed.
 */
/** `narration` = an unattributed stage direction, e.g. "(Examination performed.)". */
export type DialogueSpeaker = 'clinician' | 'patient' | 'narration';

export interface DialogueTurn {
  speaker: DialogueSpeaker;
  words: string[];
  /** Index of this turn's first word within the flat transcript word list. */
  startIndex: number;
}

/** `Clinician:` / `Doctor:` / `Dr:` / `Patient:` / `Pt:` line prefix (any case). */
const SPEAKER_LINE = /^\s*(clinician|doctor|dr|patient|pt)\b\s*:\s*(.*)$/i;

/** A standalone parenthesised line — a stage direction, not speech. */
const NARRATION_LINE = /^\s*\(.+\)\s*$/;

// Breaks single-paragraph transcripts into lines at inline speaker tags and
// stage directions. Capitalised tags only, so "told the patient: rest" stays intact.
const INLINE_SPEAKER_BREAK =
  /(?<=\S)[ \t]+(?=(?:Clinician|Doctor|Dr|Patient|Pt)[ \t]*:)/g;
const INLINE_NARRATION_OPEN = /(?<=[.?!])[ \t]+(?=\()/g;
const INLINE_NARRATION_CLOSE = /(?<=[.?!]\))[ \t]+(?=\S)/g;

function normalizeInlineTags(text: string): string {
  return text
    .replace(INLINE_SPEAKER_BREAK, '\n')
    .replace(INLINE_NARRATION_OPEN, '\n')
    .replace(INLINE_NARRATION_CLOSE, '\n');
}

/** Matches a word that ends a sentence (optionally followed by a quote/bracket). */
const SENTENCE_END = /[.?!]["')\]]?$/;

interface RawTurn {
  speaker: DialogueSpeaker;
  text: string;
}

/** Speaker-tagged parse: group lines by their `Speaker:` prefix. */
function parseTaggedTurns(text: string): RawTurn[] {
  const turns: RawTurn[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const match = line.match(SPEAKER_LINE);
    if (match) {
      const label = (match[1] ?? '').toLowerCase();
      const speaker: DialogueSpeaker = label.startsWith('p') ? 'patient' : 'clinician';
      turns.push({ speaker, text: (match[2] ?? '').trim() });
    } else if (NARRATION_LINE.test(line)) {
      // Stage direction, e.g. "(Examination and ECG performed.)" — its own
      // unattributed turn rather than a continuation of the prior speaker.
      turns.push({ speaker: 'narration', text: line });
    } else {
      // Wrapped continuation of the previous turn.
      const prev = turns[turns.length - 1];
      if (prev) prev.text = prev.text ? `${prev.text} ${line}` : line;
    }
  }
  return turns;
}

/** Fallback for single-narration transcripts: sentence split, alternate speakers. */
function fallbackTurns(text: string): RawTurn[] {
  const words = text.split(/\s+/).filter(Boolean);
  const sentences: string[][] = [];
  let current: string[] = [];
  for (const word of words) {
    current.push(word);
    if (SENTENCE_END.test(word)) {
      sentences.push(current);
      current = [];
    }
  }
  if (current.length > 0) sentences.push(current);
  return sentences.map((sentenceWords, i) => ({
    speaker: i % 2 === 0 ? 'clinician' : 'patient',
    text: sentenceWords.join(' '),
  }));
}

export function transcriptToDialogue(text: string): DialogueTurn[] {
  const tagged = parseTaggedTurns(normalizeInlineTags(text));
  const source = tagged.length > 0 ? tagged : fallbackTurns(text);

  let index = 0;
  const turns: DialogueTurn[] = [];
  for (const turn of source) {
    const words = turn.text.split(/\s+/).filter(Boolean);
    if (words.length === 0) continue;
    turns.push({ speaker: turn.speaker, words, startIndex: index });
    index += words.length;
  }
  return turns;
}

/** Flat word list (speaker labels excluded) — the reveal counts over this. */
export function transcriptDialogueWords(text: string): string[] {
  return transcriptToDialogue(text).flatMap((turn) => turn.words);
}
